import { useQuery } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  DoubleMaterialityAssessmentsControllerGetAllAssessments200Response,
  AssessmentDto,
} from "@green0meter/api-client";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityAssessmentsApi } from "@api/services";
import {
  TANSTACK_QUERY_STALE_TIME,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from "@constants";


const allowedRoles = [UserRole.Manager, UserRole.Owner];
const ACTION_GET_ASSESSMENTS = "GET_ASSESSMENTS";

interface GetAssessmentsRequest {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: string;
  year: number;
}


interface GetAssessmentsResponse {
  data: DoubleMaterialityAssessmentsControllerGetAllAssessments200Response["items"];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
}

const getAssessments = async (
  params: GetAssessmentsRequest,
): Promise<GetAssessmentsResponse> => {
  const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE;
  const pageNumber = params?.pageNumber ?? DEFAULT_PAGE_NUMBER;

  const response =
    await doubleMaterialityAssessmentsApi.doubleMaterialityAssessmentsControllerGetAllAssessments(
      params.year,
      params?.search ?? null,
      params?.orderBy ?? null,
      params?.orderDirection ?? null,
      pageNumber,
      pageSize,
    );

  return {
    data: response.data.items,
    totalItems: response.data.totalCount,
    pageNumber,
    pageSize,
  };
};

export const useGetAssessments = (
  user: UserProfileDto | null,
  params: GetAssessmentsRequest,
): {
  assessments?: GetAssessmentsResponse;
  assessmentsEnabled: boolean;
  isPendingGetAssessments: boolean;
  refetchAssessments: () => void;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const { isPending, data, refetch } = useQuery({
    queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_ASSESSMENTS, params],
    queryFn: () => getAssessments(params),
    staleTime: TANSTACK_QUERY_STALE_TIME,
    enabled,
  });

  return {
    assessments: data,
    assessmentsEnabled: enabled,
    isPendingGetAssessments: isPending,
    refetchAssessments: refetch,
  };
};
